import React, { useEffect, useState } from 'react';
import { Box, Divider, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import Menu from '@mui/material/Menu';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import {
    addDays,
    endOfMonth,
    endOfWeek,
    format,
    startOfMonth,
    startOfWeek,
    subDays
} from 'date-fns';
import { es } from 'date-fns/locale';
import TextButton from '@components/Utilities/Buttons/TextButton';

interface RangeDatePickerProps {
    value?: string;
    fullWidth?: boolean;
    /**
     * Modo de UN SOLO DÍA: un calendario en vez de dos, solo los atajos de día y
     * el menú se cierra al elegir. Misma caja y mismo menú que el modo rango para
     * que se sienta igual en toda la app (lo usa el panel de cajas).
     */
    singleDay?: boolean;
    /** Día seleccionado en modo `singleDay`: el calendario abre ahí */
    selectedDate?: Date;
    onChangeStartDate?: (date: Date) => void;
    onChangeEndDate?: (date: Date) => void;
    onChangeRange?: (startDate: Date, endDate: Date) => void;
    /** Modo `singleDay`: devuelve el día elegido a las 00:00 */
    onChangeDay?: (date: Date) => void;
}

enum ShortcutTypes {
    Tomorrow = 'tomorrow',
    Today = 'today',
    Yesterday = 'yesterday',
    Week = 'week',
    LastWeek = 'lastWeek',
    Month = 'month',
    LastMonth = 'lastMonth',
    Year = 'year',
    LastYear = 'lastYear'
}

const menuId = 'date-range-picker-menu';

const today = new Date();
today.setHours(0, 0, 0, 0);

const endToday = new Date();
endToday.setHours(23, 59, 59, 59);

const RangeDatePicker: React.FunctionComponent<RangeDatePickerProps> = (props) => {
    const { value, fullWidth, singleDay, selectedDate, onChangeStartDate, onChangeEndDate, onChangeRange, onChangeDay } =
        props;
    const [anchorEl, setAnchorEl] = useState(null);
    const isMenuOpen = Boolean(anchorEl);
    const [startDate, setStartDate] = useState<Date>(selectedDate || today);
    const [endDate, setEndDate] = useState<Date>(endToday);
    const [option, setOption] = useState<number>(0);

    // En modo un día el dueño del estado es el padre: si cambia allá (por ejemplo
    // al recargar la pantalla) el calendario tiene que seguirlo.
    useEffect(() => {
        if (singleDay && selectedDate) {
            setStartDate(selectedDate);
        }
    }, [singleDay, selectedDate]);

    const onPickDay = (day: Date) => {
        const picked = new Date(day);
        picked.setHours(0, 0, 0, 0);
        setStartDate(picked);
        if (onChangeDay) onChangeDay(picked);
        setAnchorEl(null);
    };

    const onShortcut = (type: ShortcutTypes) => {
        let d1 = today;
        let d2 = endToday;
        switch (type) {
            case ShortcutTypes.Tomorrow:
                d1 = addDays(today, 1);
                d2 = addDays(endToday, 1);
                setOption(-1);
                break;
            case ShortcutTypes.Today:
                d1 = today;
                d2 = endToday;
                setOption(0);
                break;
            case ShortcutTypes.Yesterday:
                d1 = subDays(today, 1);
                d2 = subDays(endToday, 1);
                setOption(1);
                break;
            case ShortcutTypes.Week:
                d1 = startOfWeek(today);
                d2 = endOfWeek(today);
                setOption(2);
                break;
            case ShortcutTypes.LastWeek:
                d1 = subDays(startOfWeek(today), 7);
                d2 = subDays(endOfWeek(today), 7);
                setOption(3);
                break;
            case ShortcutTypes.Month:
                d1 = startOfMonth(today);
                d2 = endOfMonth(today);
                setOption(4);
                break;
            case ShortcutTypes.LastMonth:
                const day1LastMonth = subDays(startOfMonth(today), 1);
                d1 = startOfMonth(day1LastMonth);
                d2 = endOfMonth(day1LastMonth);
                setOption(5);
                break;
        }
        d1.setHours(0, 0, 0, 0);
        d2.setHours(23, 59, 59);
        if (singleDay) {
            onPickDay(d1);
            return;
        }
        setStartDate(d1);
        setEndDate(d2);
        if (onChangeRange) onChangeRange(d1, d2);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const handleRangeMenuOpen = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            sx={{ marginTop: 2 }}
            id={menuId}
            keepMounted
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            <Stack direction="row" spacing={2}>
                <Stack direction="column" p={2} spacing={2}>
                    <TextButton selected={option === -1} onClick={() => onShortcut(ShortcutTypes.Tomorrow)}>
                        Mañana
                    </TextButton>
                    <TextButton selected={option === 0} onClick={() => onShortcut(ShortcutTypes.Today)}>
                        Hoy
                    </TextButton>
                    <TextButton selected={option === 1} onClick={() => onShortcut(ShortcutTypes.Yesterday)}>
                        Ayer
                    </TextButton>
                    {/* Los atajos de rango no aplican cuando se elige un solo día */}
                    {!singleDay && (
                        <>
                            <TextButton selected={option === 2} onClick={() => onShortcut(ShortcutTypes.Week)}>
                                Semana actual
                            </TextButton>
                            <TextButton selected={option === 3} onClick={() => onShortcut(ShortcutTypes.LastWeek)}>
                                Semana pasada
                            </TextButton>
                            <TextButton selected={option === 4} onClick={() => onShortcut(ShortcutTypes.Month)}>
                                Mes actual
                            </TextButton>
                            <TextButton selected={option === 5} onClick={() => onShortcut(ShortcutTypes.LastMonth)}>
                                Mes pasado
                            </TextButton>
                        </>
                    )}
                </Stack>
                <Divider flexItem orientation="vertical" />
                <Stack direction="column" py={2}>
                    <Stack direction="row" spacing={4}>
                        <Stack direction="column">
                            <Typography fontSize={14} color="gray">
                                {singleDay ? 'Día' : 'Desde'}
                            </Typography>
                            <TextField
                                contentEditable={false}
                                variant="outlined"
                                value={format(startDate ? startDate : new Date(), 'dd MMMM, yyy', { locale: es })}
                            />
                        </Stack>
                        {!singleDay && (
                            <Stack direction="column">
                                <Typography fontSize={14} color="gray">
                                    Hasta
                                </Typography>
                                <TextField
                                    contentEditable={false}
                                    variant="outlined"
                                    value={format(endDate ? endDate : new Date(), 'dd MMMM, yyy', { locale: es })}
                                />
                            </Stack>
                        )}
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                            <DateCalendar
                                value={startDate}
                                maxDate={singleDay ? undefined : endDate}
                                onChange={(newStartDate) => {
                                    if (!newStartDate) return;
                                    if (singleDay) {
                                        onPickDay(newStartDate);
                                        return;
                                    }
                                    newStartDate.setHours(0, 0, 0, 0);
                                    setStartDate(newStartDate);
                                    if (onChangeStartDate) onChangeStartDate(newStartDate);
                                }}
                            />
                        </LocalizationProvider>
                        {!singleDay && (
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                                <DateCalendar
                                    value={endDate}
                                    minDate={startDate}
                                    onChange={(newEndDate) => {
                                        if (newEndDate) {
                                            newEndDate.setHours(23, 59, 59);
                                            setEndDate(newEndDate);
                                            if (onChangeEndDate) onChangeEndDate(newEndDate);
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Menu>
    );

    return (
        <Box>
            <TextField
                fullWidth={fullWidth}
                contentEditable={false}
                variant="outlined"
                value={value}
                onClick={handleRangeMenuOpen}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <DateRangeIcon />
                        </InputAdornment>
                    )
                }}
            />
            {renderMenu}
        </Box>
    );
};

export default RangeDatePicker;
